import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { API } from "../../App";

/**
 * Admin component for configuring automatic lesson scheduling.
 * Allows setting days of the week, time slots, and default lesson properties.
 */
const AdminSchedule = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dayLabels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/schedule`, { withCredentials: true });
      setConfig(res.data);
    } catch (err) { 
      console.error("Error fetching schedule config:", err);
      toast.error("Erro ao carregar configuração"); 
    }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/schedule`, config, { withCredentials: true });
      toast.success(res.data.message);
    } catch (err) { 
      console.error("Error saving schedule config:", err);
      toast.error("Erro ao guardar"); 
    }
    finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    try {
      const res = await axios.post(`${API}/admin/schedule/generate`, {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch (err) { 
      console.error("Error generating lessons:", err);
      toast.error("Erro ao gerar aulas"); 
    }
  };

  const toggleDay = (dayIdx) => {
    setConfig(prev => ({
      ...prev,
      days: prev.days.includes(dayIdx) ? prev.days.filter(d => d !== dayIdx) : [...prev.days, dayIdx].sort()
    }));
  };

  const updateSlot = (idx, field, value) => {
    setConfig(prev => {
      const slots = [...prev.slots];
      slots[idx] = { ...slots[idx], [field]: value };
      return { ...prev, slots };
    });
  };

  const addSlot = () => {
    setConfig(prev => ({ ...prev, slots: [...prev.slots, { start_time: "09:00", end_time: "11:00", time_slot: "morning" }] }));
  };

  const removeSlot = (idx) => {
    setConfig(prev => ({ ...prev, slots: prev.slots.filter((_, i) => i !== idx) }));
  };

  if (loading || !config) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#6697a4] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#3d5a63]">Aulas Automáticas</h2>
          <p className="text-sm text-[#94A3B8]">Gerar aulas automaticamente por semana</p>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-sm">Ativo</Label>
          <Switch checked={config.enabled} onCheckedChange={(v) => setConfig({ ...config, enabled: v })} data-testid="schedule-enabled" />
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 space-y-6">
          {/* Days */}
          <div>
            <Label className="mb-2 block">Dias da semana</Label>
            <div className="flex flex-wrap gap-2">
              {dayLabels.map((label, idx) => (
                <button 
                  key={`day-${idx}`} 
                  type="button" 
                  onClick={() => toggleDay(idx)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    config.days.includes(idx) ? "bg-[#6697a4] text-white" : "bg-slate-100 text-[#475569] hover:bg-slate-200"
                  }`} 
                  data-testid={`schedule-day-${idx}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Slots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Horários</Label>
              <Button size="sm" variant="outline" onClick={addSlot} data-testid="add-slot-btn">
                <Plus className="h-3 w-3 mr-1" /> Horário
              </Button>
            </div>
            <div className="space-y-2">
              {config.slots.map((slot, idx) => (
                <div key={`slot-${idx}-${slot.start_time}`} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg" data-testid={`schedule-slot-${idx}`}>
                  <Input type="time" value={slot.start_time} onChange={e => updateSlot(idx, "start_time", e.target.value)} className="flex-1 min-w-0" />
                  <span className="text-sm text-[#94A3B8] shrink-0">às</span>
                  <Input type="time" value={slot.end_time} onChange={e => updateSlot(idx, "end_time", e.target.value)} className="flex-1 min-w-0" />
                  {config.slots.length > 1 && (
                    <Button size="sm" variant="ghost" className="text-red-500 shrink-0 h-8 w-8 p-0" onClick={() => removeSlot(idx)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Defaults */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Título padrão</Label>
              <Input value={config.default_title_surf} onChange={e => setConfig({ ...config, default_title_surf: e.target.value })} data-testid="schedule-title" />
            </div>
            <div>
              <Label>Modalidade</Label>
              <select 
                value={config.default_category} 
                onChange={e => setConfig({ ...config, default_category: e.target.value })} 
                className="w-full h-10 px-3 border rounded-lg text-sm" 
                data-testid="schedule-category"
              >
                <option value="both">Surf & Bodyboard</option>
                <option value="surf">Apenas Surf</option>
                <option value="bodyboard">Apenas Bodyboard</option>
              </select>
            </div>
            <div>
              <Label>Máx. alunos</Label>
              <Input 
                type="number" 
                value={config.default_max_students} 
                onChange={e => setConfig({ ...config, default_max_students: e.target.value === '' ? '' : parseInt(e.target.value) })} 
                onBlur={e => setConfig({ ...config, default_max_students: parseInt(e.target.value) || 8 })} 
                data-testid="schedule-max" 
              />
            </div>
            <div>
              <Label>Preço (€)</Label>
              <Input 
                type="number" 
                value={config.default_price} 
                onChange={e => setConfig({ ...config, default_price: e.target.value === '' ? '' : parseFloat(e.target.value) })} 
                onBlur={e => setConfig({ ...config, default_price: parseFloat(e.target.value) || 25 })} 
                data-testid="schedule-price" 
              />
            </div>
            <div>
              <Label>Semanas à frente</Label>
              <Input 
                type="number" 
                min={1} 
                max={8} 
                value={config.weeks_ahead} 
                onChange={e => setConfig({ ...config, weeks_ahead: e.target.value === '' ? '' : parseInt(e.target.value) })} 
                onBlur={e => setConfig({ ...config, weeks_ahead: parseInt(e.target.value) || 2 })} 
                data-testid="schedule-weeks" 
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <select 
                value={config.default_lesson_type} 
                onChange={e => setConfig({ ...config, default_lesson_type: e.target.value })} 
                className="w-full h-10 px-3 border rounded-lg text-sm" 
                data-testid="schedule-type"
              >
                <option value="group">Grupo</option>
                <option value="private">Privada</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="save-schedule-btn">
          <Save className="mr-2 h-4 w-4" /> {saving ? "A guardar..." : "Guardar Configuração"}
        </Button>
        <Button onClick={handleGenerate} variant="outline" data-testid="generate-lessons-btn">
          Gerar Aulas Agora
        </Button>
      </div>
    </div>
  );
};

export default AdminSchedule;
